import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "@mui/icons-material";
import {
  PredictionLeaderboardRow,
  getDivisionStandingsAll,
  getPredictionLeaderboard,
} from "../../services/MatchService";
import { currentSeason } from "../../Constants";
import { AuthContext } from "../../context/AuthContext";
import { hasGamesPlayed } from "../../utils/predictionScore";
import { getSimpleDivisionLabel } from "../../utils/divisionUtils";
import {
  FIRST_PREDICTION_SEASON,
  StandingsByDivision,
  divisionLabel,
  majorLabel,
  majorOf,
  majorsOf,
  predictableDivisions,
} from "./predict/predictShared";
import styles from "./Predict.module.css";

const SEASONS = Array.from(
  { length: currentSeason - FIRST_PREDICTION_SEASON + 1 },
  (_, i) => currentSeason - i
);
const TOP = 15;

type RankedRow = PredictionLeaderboardRow & { rank: number };

const scoreClass = (score: number) =>
  score <= 1.5 ? styles.scoreGood : score <= 2.5 ? styles.scoreMid : styles.scoreLow;

/**
 * /standings/leaderboard — who predicts best. Samlet averages each player's divisions; with "Kun fuld
 * pakke" on it only ranks players with a pick in every division, so one lucky division can't top it.
 * ?season= and ?division= (a single division or group key) keep the view in the URL.
 */
const PredictionLeaderboardPage: React.FC = () => {
  const { t } = useTranslation(["pages", "common"]);
  const { user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const seasonParam = Number(searchParams.get("season"));
  const season = SEASONS.includes(seasonParam) ? seasonParam : currentSeason;
  const divisionParam = searchParams.get("division") ?? "";

  const [standings, setStandings] = useState<{ season: number; data: StandingsByDivision } | null>(null);
  const [board, setBoard] = useState<{ key: string; rows: PredictionLeaderboardRow[] } | null>(null);
  const [failed, setFailed] = useState(false);
  const [fullOnly, setFullOnly] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDivisionStandingsAll(season)
      .then((data: StandingsByDivision) => {
        if (!cancelled) setStandings({ season, data: data ?? {} });
      })
      .catch(() => {
        if (!cancelled) setStandings({ season, data: {} });
      });
    return () => {
      cancelled = true;
    };
  }, [season]);

  const table = standings?.season === season ? standings.data : null;
  const divisions = useMemo(() => predictableDivisions(table), [table]);
  const majors = majorsOf(divisions);
  // A major with groups also has an "Alle" view (division=3div): only players with a pick in every
  // group of it, like the old "3. Division (Alle - kraever alle 4)" filter.
  const groupedMajors = majors.filter((m) => m.divisions.length > 1).map((m) => m.major);
  const division = divisions.includes(divisionParam) || groupedMajors.includes(divisionParam) ? divisionParam : "";
  const isOverall = !division;
  const isGroupAll = groupedMajors.includes(division);
  const viewLabel = isGroupAll ? getSimpleDivisionLabel(division) : divisionLabel(division);
  const boardKey = `${season}|${division}`;

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setExpanded(false);
    getPredictionLeaderboard(season, division || undefined)
      .then((rows) => {
        if (!cancelled) setBoard({ key: boardKey, rows });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [season, division, boardKey]);

  const rows = board?.key === boardKey ? board.rows : null;
  const coverageOf = (row: PredictionLeaderboardRow) => row.predictedDivisions ?? row.divisions;
  // An older backend doesn't report predictedDivisions and never scores ligaen, so nobody could pass
  // the full-set filter. Offer it only when the rows can answer it.
  const coverageKnown = !!rows && rows.some((r) => r.predictedDivisions !== undefined);
  const filterFull = isOverall && fullOnly && coverageKnown;
  const divisionSet = useMemo(() => new Set(divisions), [divisions]);
  const coveredCount = (row: PredictionLeaderboardRow) => coverageOf(row).filter((d) => divisionSet.has(d)).length;
  const isFull = (row: PredictionLeaderboardRow) => divisions.length > 0 && coveredCount(row) === divisions.length;

  const ranked: RankedRow[] = useMemo(() => {
    if (!rows) return [];
    const list = filterFull ? rows.filter(isFull) : rows;
    return list.map((row, i) => ({ ...row, rank: i + 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filterFull, divisions]);

  const meId = user?.steamID;
  const meIndex = meId ? ranked.findIndex((r) => r.steamId === meId) : -1;
  const shown = expanded ? ranked : ranked.slice(0, TOP);
  const pinned = !expanded && meIndex >= TOP ? ranked[meIndex] : null;
  const participants = rows ? new Set(rows.map((r) => r.steamId)).size : 0;

  const setParams = (next: { season?: number; division?: string }) => {
    const s = next.season ?? season;
    const d = next.division ?? division;
    const params: Record<string, string> = {};
    if (s !== currentSeason) params.season = String(s);
    if (d) params.division = d;
    setSearchParams(params);
  };

  const openMajor = division ? majorOf(division) : "";
  const groups = majors.find((m) => m.major === openMajor)?.divisions ?? [];
  const pillClass = (active: boolean) => `${styles.pill} ${active ? styles.pillActive : ""}`;
  const colsClass = isOverall ? styles.lbColsAll : styles.lbCols;
  const rowCols = isOverall ? styles.lbRowAll : "";

  const renderRow = (row: RankedRow, gap = false) => {
    const me = row.steamId === meId;
    const covered = new Set(coverageOf(row));
    const params = new URLSearchParams();
    if (season !== currentSeason) params.set("season", String(season));
    if (division) params.set("division", division);
    const query = params.toString();
    const openPredictions = () => navigate(`/standings/predict/${row.steamId}${query ? `?${query}` : ""}`);
    // As before the redesign: the row opens the player's predictions, the name opens their profile.
    return (
      <div
        key={`${row.steamId}${gap ? "-pinned" : ""}`}
        role="link"
        tabIndex={0}
        aria-label={t("predict.userTitle", { name: row.playerName })}
        onClick={openPredictions}
        onKeyDown={(e) => {
          if (e.key === "Enter") openPredictions();
        }}
        className={[styles.lbRow, rowCols, me ? styles.lbRowMe : "", gap ? styles.lbRowGap : ""].join(" ")}
      >
        <span className={`${styles.lbRank} ${row.rank === 1 ? styles.lbRankFirst : row.rank <= 3 ? styles.lbRankTop : ""}`}>
          {row.rank}
        </span>
        <span className={styles.lbPlayer}>
          <Link className={styles.lbName} to={`/player/${row.steamId}`} onClick={(e) => e.stopPropagation()}>
            {row.playerName}
          </Link>
          {me && <span className={styles.meBadge}>{t("predictLeaderboard.me")}</span>}
        </span>
        {isOverall && (
          <span className={`${styles.coverage} ${styles.coverageCell}`}>
            <span
              className={styles.coverageSquares}
              aria-label={t("predictLeaderboard.coveredOf", { count: coveredCount(row), total: divisions.length })}
            >
              {divisions.map((d) => (
                <span key={d} title={divisionLabel(d)} className={`${styles.coverageSquare} ${covered.has(d) ? styles.coverageOn : ""}`} />
              ))}
            </span>
            <span className={styles.coverageText}>
              {coveredCount(row)}/{divisions.length}
            </span>
          </span>
        )}
        <span className={styles.num}>{row.exactHits ?? "–"}</span>
        <span className={`${styles.lbScore} ${scoreClass(row.averageScore)}`}>{row.averageScore.toFixed(2)}</span>
      </div>
    );
  };

  // "Din placering"
  const meAll = meId && rows ? rows.find((r) => r.steamId === meId) : undefined;
  const divisionScored = isGroupAll
    ? groups.some((d) => hasGamesPlayed(table?.[d]))
    : division
      ? hasGamesPlayed(table?.[division])
      : true;
  let you: React.ReactNode;
  if (!user) {
    you = <span className={styles.youTitle}>{t("predictLeaderboard.notSignedIn")}</span>;
  } else if (!rows) {
    you = <span className={styles.bodyText}>{t("predictLeaderboard.loading")}</span>;
  } else if (meIndex >= 0) {
    const me = ranked[meIndex];
    you = (
      <>
        <div className={styles.youRank}>
          #{me.rank} <span className={styles.youOf}>{t("predictLeaderboard.of", { count: ranked.length })}</span>
        </div>
        <div className={styles.youStats}>
          <div className={styles.statBox}>
            <div className={styles.statLabel}>{t("predictLeaderboard.colScore")}</div>
            <div className={styles.statValue}>{me.averageScore.toFixed(2)}</div>
          </div>
          <div className={styles.statBox}>
            <div className={styles.statLabel}>{t("predictLeaderboard.colExact")}</div>
            <div className={styles.statValue}>{me.exactHits ?? "–"}</div>
          </div>
        </div>
        <p className={styles.bodyText}>
          {isOverall
            ? t("predictLeaderboard.coveredOf", { count: coveredCount(me), total: divisions.length })
            : viewLabel}
        </p>
      </>
    );
  } else if (filterFull && meAll) {
    you = (
      <>
        <span className={styles.youTitle}>{t("predictLeaderboard.notInOverall")}</span>
        <p className={styles.bodyText}>
          {t("predictLeaderboard.notInOverallText", {
            total: divisions.length,
            count: coveredCount(meAll),
          })}
        </p>
      </>
    );
  } else if (division && divisionScored) {
    you = <span className={styles.youTitle}>{t("predictLeaderboard.notInDivision", { division: viewLabel })}</span>;
  } else {
    you = (
      <>
        <span className={styles.youTitle}>{t("predictLeaderboard.notRanked")}</span>
        <p className={styles.bodyText}>{t("predictLeaderboard.notRankedText")}</p>
      </>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <Link className={styles.backLink} to={season === currentSeason ? "/standings/predict" : `/standings/predict?season=${season}`}>
            <ChevronLeft sx={{ fontSize: 18 }} />
            {t("predictLeaderboard.back")}
          </Link>
          <h1 className={styles.title}>{t("predictLeaderboard.title")}</h1>
          <div className={styles.statusLine}>
            <span>{rows ? t("predictLeaderboard.participants", { count: participants, season }) : " "}</span>
          </div>
        </div>
        <div className={styles.headRight}>
          <div role="group" aria-label={t("predict.seasonAria")} className={styles.pillGroup}>
            {SEASONS.map((s) => (
              <button
                key={s}
                type="button"
                className={pillClass(s === season)}
                aria-pressed={s === season}
                onClick={() => s !== season && setParams({ season: s, division: "" })}
              >
                {s === season ? t("common:seasonNum", { number: s }) : s}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.controls}>
        <div role="group" aria-label={t("predict.divisionAria")} className={styles.pillGroup}>
          <button type="button" className={pillClass(isOverall)} aria-pressed={isOverall} onClick={() => setParams({ division: "" })}>
            {t("predictLeaderboard.overall")}
          </button>
          {majors.map((m) => {
            const active = m.major === openMajor;
            return (
              <button
                key={m.major}
                type="button"
                className={pillClass(active)}
                aria-pressed={active}
                onClick={() => !active && setParams({ division: m.divisions.length > 1 ? m.major : m.divisions[0] })}
              >
                {majorLabel(m.major)}
              </button>
            );
          })}
        </div>
        {groups.length > 1 && (
          <div role="group" aria-label={t("predict.groupAria")} className={styles.pillGroup}>
            <button type="button" className={pillClass(isGroupAll)} aria-pressed={isGroupAll} onClick={() => setParams({ division: openMajor })}>
              {t("predict.all")}
            </button>
            {groups.map((d) => (
              <button key={d} type="button" className={pillClass(d === division)} aria-pressed={d === division} onClick={() => setParams({ division: d })}>
                {d.slice(-1)}
              </button>
            ))}
          </div>
        )}
        <span className={styles.spacer} />
        {isOverall && coverageKnown && (
          <button
            type="button"
            className={`${styles.toggle} ${fullOnly ? styles.toggleOn : ""}`}
            aria-pressed={fullOnly}
            onClick={() => setFullOnly((v) => !v)}
          >
            <span className={styles.toggleTrack}>
              <span className={styles.toggleKnob} />
            </span>
            {t("predictLeaderboard.fullOnly")}
          </button>
        )}
      </div>

      <div className={styles.lbLayout}>
        <section className={styles.panel} aria-label={t("predictLeaderboard.title")}>
          <div className={`${styles.colHead} ${colsClass}`}>
            <span className={styles.center}>#</span>
            <span>{t("predictLeaderboard.colPlayer")}</span>
            {isOverall && <span className={styles.coverageCell}>{t("predictLeaderboard.colDivisions")}</span>}
            <span className={styles.right}>{t("predictLeaderboard.colExact")}</span>
            <span className={styles.right}>{t("predictLeaderboard.colScore")}</span>
          </div>

          {failed ? (
            <div className={styles.loadingText}>{t("predictLeaderboard.loadError")}</div>
          ) : !rows ? (
            <div className={styles.loadingText}>{t("predictLeaderboard.loading")}</div>
          ) : ranked.length === 0 ? (
            <div className={styles.poolEmpty}>
              <span className={styles.poolEmptyTitle}>{t("predictLeaderboard.empty")}</span>
              <span className={styles.poolEmptyText}>
                {filterFull && rows.length > 0
                  ? t("predictLeaderboard.emptyFull", { total: divisions.length })
                  : t("predictLeaderboard.emptyText")}
              </span>
            </div>
          ) : (
            <>
              <div className={styles.lbRows}>
                {shown.map((row) => renderRow(row))}
                {pinned && renderRow(pinned, true)}
              </div>
              {ranked.length > TOP && (
                <div className={styles.expandRow}>
                  <button type="button" className={`${styles.ghostButton} ${styles.expandButton}`} onClick={() => setExpanded((v) => !v)}>
                    {expanded ? t("predictLeaderboard.showTop") : t("predictLeaderboard.showAll", { count: ranked.length })}
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        <aside className={styles.sideStack}>
          <section className={styles.panel} aria-label={t("predictLeaderboard.yourPlace")}>
            <div className={styles.panelLabel}>{t("predictLeaderboard.yourPlace")}</div>
            <div className={styles.youBody}>{you}</div>
          </section>
          <section className={styles.panel} aria-label={t("predictLeaderboard.howTitle")}>
            <div className={styles.panelLabel}>{t("predictLeaderboard.howTitle")}</div>
            <div className={styles.explainBody}>
              <p className={styles.bodyText}>{t("predictLeaderboard.how1")}</p>
              <p className={styles.bodyText}>{t("predictLeaderboard.how2")}</p>
              <p className={styles.bodyText}>
                {isOverall
                  ? filterFull
                    ? t("predictLeaderboard.howOverallFull", { total: divisions.length })
                    : t("predictLeaderboard.howOverallAll")
                  : isGroupAll
                    ? t("predictLeaderboard.howGroupAll")
                    : t("predictLeaderboard.howDivision")}
              </p>
              <p className={styles.bodyText}>{t("predictLeaderboard.howUnscored")}</p>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default PredictionLeaderboardPage;
